import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  CircularProgress,
  Autocomplete,
  Alert
} from '@mui/material';
import { createWorkPackage, updateWorkPackage } from 'shared/utils/proposalApi';
import { ProfileDatePicker } from 'components/social/profile/ProfileDialogs';

const WorkPackageDialog = ({ open, onClose, proposalId, workPackage, onSuccess, consortiumMembers = [] }) => {
  const isEdit = Boolean(workPackage);

  const [title, setTitle] = useState('');
  const [number, setNumber] = useState('');
  const [wpLeader, setWpLeader] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (workPackage) {
      setTitle(workPackage.title || '');
      setNumber(workPackage.number || '');
      setStartDate(workPackage.start_date || '');
      setEndDate(workPackage.end_date || '');
      setDescription(workPackage.description || '');
    } else {
      setTitle('');
      setNumber('');
      setStartDate('');
      setEndDate('');
      setDescription('');
      setWpLeader(null);
    }
    setError(null);
  }, [workPackage, open]);

  // Sync wpLeader from ID once members are loaded
  useEffect(() => {
    if (workPackage?.wp_leader && consortiumMembers.length > 0) {
      const found = consortiumMembers.find(u => u.id === workPackage.wp_leader);
      setWpLeader(found || null);
    }
  }, [workPackage, consortiumMembers]);

  const handleSubmit = async () => {
    if (!title.trim() || !wpLeader) return;
    setIsSaving(true);
    setError(null);

    const submitData = {
      proposal: proposalId,
      title: title.trim(),
      wp_leader: wpLeader.id,
      ...(isEdit && {
        number: number !== '' ? parseInt(number) : undefined,
        start_date: startDate || null,
        end_date: endDate || null,
        description: description || null,
      }),
    };

    const result = isEdit
      ? await updateWorkPackage(workPackage.id, submitData)
      : await createWorkPackage(submitData);

    if (result.success) {
      onSuccess?.(result.data);
      onClose();
    } else {
      setError(result.error);
    }
    setIsSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 3000 }}>
      <DialogTitle>{isEdit ? 'Edit Work Package' : 'Add Work Package'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>

          <TextField
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
            autoFocus
            fullWidth
          />

          <Autocomplete
            options={consortiumMembers}
            getOptionLabel={opt => opt ? `${opt.name || opt.username || 'Unknown'}${opt.org_short_name ? ` (${opt.org_short_name})` : ''}` : ''}
            value={wpLeader}
            slotProps={{ popper: { style: { zIndex: 3001 } } }}
            onChange={(_, v) => setWpLeader(v)}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            renderInput={params => (
              <TextField
                {...params}
                label="Work Package Leader"
                required
              />
            )}
          />

          {isEdit && (
            <>
              <TextField
                label="WP Number"
                type="number"
                value={number}
                onChange={e => setNumber(e.target.value)}
                helperText="Auto-assigned on create"
                fullWidth
              />

              <Stack direction="row" spacing={2}>
                <ProfileDatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                />
                <ProfileDatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                />
              </Stack>

              <TextField
                label="Description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                multiline
                rows={4}
                fullWidth
              />
            </>
          )}

          {error && (
            <Alert severity="error">
              {typeof error === 'string' ? error : JSON.stringify(error)}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving || !title.trim() || !wpLeader}
        >
          {isSaving ? <CircularProgress size={20} /> : (isEdit ? 'Update' : 'Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkPackageDialog;
